import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Newspaper, Save, X, Sparkles, Wand2, Upload, Eye, EyeOff, Tag, Star,
  Loader2, FileText, ImageIcon, Image, Info, ClipboardPaste, Trash2, Zap
} from 'lucide-react';
import { JewelModal, JewelInput, JewelTextarea, JewelSelect, JewelButton, JewelField } from '../ui/jewel-modal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['Announcement', 'Update', 'Event', 'Group News', 'Partnership', 'Culture', 'Product', 'Other'];

const EMPTY = {
  title: '', content: '', excerpt: '', category: '', image_url: '',
  published: true, is_featured: false, tags: [], slug: ''
};

const getImgUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  if (path.startsWith('/api/uploads/')) return `${BACKEND_URL}${path}`;
  if (path.startsWith('/uploads/')) return `${BACKEND_URL}/api${path}`;
  return `${BACKEND_URL}${path}`;
};

const readTime = (text) => {
  const words = (text || '').trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 225));
};

/**
 * Premium news article composer — Jewel & Luxury theme.
 * • AI-assist on title, content, excerpt, tags
 * • Live preview toggle
 * • Cover image upload with drag-drop
 * • Auto read-time & word count
 * • Featured / draft / published toggles
 */
export const NewsComposer = ({ open, onOpenChange, editing, getAuthHeaders, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [titleSuggestions, setTitleSuggestions] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        title: editing.title || '', content: editing.content || '',
        excerpt: editing.excerpt || '', category: editing.category || '',
        image_url: editing.image_url || '', published: editing.published !== false,
        is_featured: !!editing.is_featured, tags: editing.tags || [], slug: editing.slug || '',
      });
      setImagePreview(editing.image_url ? getImgUrl(editing.image_url) : null);
    } else {
      setForm(EMPTY);
      setImagePreview(null);
    }
    setImageFile(null);
    setShowPreview(false);
    setTitleSuggestions([]);
  }, [open, editing]);

  const words = (form.content || '').trim().split(/\s+/).filter(Boolean).length;
  const rt = readTime(form.content);

  const handleImage = (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be < 10MB'); return; }
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) handleImage(file);
  };

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setBusy(true);
    try {
      const headers = getAuthHeaders();
      let imageUrl = form.image_url;
      if (imageFile) {
        const fd = new FormData();
        fd.append('image', imageFile);
        const up = await axios.post(`${BACKEND_URL}/api/admin/news/upload-image`, fd, {
          headers: { Authorization: headers.Authorization },
        });
        imageUrl = up.data.image_url;
      }
      const payload = {
        ...form,
        image_url: imageUrl,
        tags: form.tags.length ? form.tags : null,
      };
      if (editing) {
        await axios.put(`${BACKEND_URL}/api/admin/news/${editing.id}`, payload, { headers });
        toast.success('Article updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/news`, payload, { headers });
        toast.success(form.published ? 'Article published' : 'Draft saved');
      }
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setBusy(false);
  };

  const runAI = async (mode) => {
    if (mode !== 'content' && !form.content.trim() && mode !== 'title') {
      toast.error('Add some content first');
      return;
    }
    if (!form.title.trim() && mode === 'content') {
      toast.error('Enter a title first — AI needs context');
      return;
    }
    setAiBusy(mode);
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${BACKEND_URL}/api/admin/news/ai-assist`, {
        mode,
        title: form.title,
        category: form.category,
        current_content: form.content,
        context: form.excerpt,
      }, { headers });
      if (mode === 'content') {
        setForm(f => ({ ...f, content: res.data.text }));
        toast.success('Article generated');
      } else if (mode === 'polish') {
        setForm(f => ({ ...f, content: res.data.text }));
        toast.success('Content polished');
      } else if (mode === 'excerpt') {
        setForm(f => ({ ...f, excerpt: res.data.text }));
        toast.success('Excerpt generated');
      } else if (mode === 'title') {
        setTitleSuggestions(res.data.items || []);
        toast.success(`${(res.data.items || []).length} title options ready`);
      } else if (mode === 'tags') {
        const existing = new Set(form.tags);
        (res.data.items || []).forEach(t => existing.add(t));
        setForm(f => ({ ...f, tags: Array.from(existing) }));
        toast.success('Tags added');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI-assist failed');
    }
    setAiBusy(null);
  };

  const addTag = (t) => {
    const clean = t.trim().toLowerCase().replace(/^#/, '').replace(/\s+/g, '-');
    if (!clean) return;
    if (form.tags.includes(clean)) return;
    setForm(f => ({ ...f, tags: [...f.tags, clean] }));
  };
  const removeTag = (t) => setForm(f => ({ ...f, tags: f.tags.filter(x => x !== t) }));

  const [tagInput, setTagInput] = useState('');

  const section = (hex) => ({
    background: `linear-gradient(135deg, ${hex}0A, transparent)`,
    borderColor: `${hex}25`,
  });

  return (
    <JewelModal
      open={open} onOpenChange={onOpenChange}
      icon={editing ? Save : Sparkles}
      title={editing ? 'Edit article' : 'New article'}
      subtitle={editing ? 'Update your story' : 'Craft a story with AI where useful'}
      accent="cyan" size="3xl" testid="news-composer"
      footer={<>
        <JewelButton variant="ghost" onClick={() => onOpenChange(false)}>Cancel</JewelButton>
        <JewelButton variant="ghost" onClick={() => setShowPreview(p => !p)} icon={showPreview ? FileText : Eye} data-testid="news-preview-toggle">
          {showPreview ? 'Edit' : 'Preview'}
        </JewelButton>
        {!form.published && (
          <JewelButton variant="ghost" onClick={() => setForm(f => ({ ...f, published: true }))}>Mark as published</JewelButton>
        )}
        <JewelButton accent="cyan" onClick={save} loading={busy} disabled={busy} icon={Save} data-testid="news-save-btn">
          {editing ? 'Update' : (form.published ? 'Publish' : 'Save draft')}
        </JewelButton>
      </>}
    >
      {showPreview ? (
        <ArticlePreview form={form} imagePreview={imagePreview} rt={rt} />
      ) : (
        <div className="space-y-4">
          {/* TITLE & META */}
          <section className="relative overflow-hidden rounded-2xl border p-4" style={section('#00E5FF')}>
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Newspaper className="w-3.5 h-3.5 text-cyan-300" />
                <p className="text-[10px] tracking-[0.2em] uppercase text-cyan-200/80 font-semibold">Article</p>
              </div>
              <AIChip mode="title" busy={aiBusy} onClick={() => runAI('title')} icon={Wand2} label="Suggest titles" />
            </div>
            <div className="space-y-3">
              <JewelField label="Title" required>
                <JewelInput value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="A crisp, engaging headline" data-testid="article-title-input" autoFocus />
              </JewelField>
              {titleSuggestions.length > 0 && (
                <div className="p-3 rounded-xl bg-cyan-500/[0.05] border border-cyan-500/25 space-y-1.5" data-testid="title-suggestions">
                  <p className="text-[9px] tracking-[0.14em] uppercase text-cyan-300 font-bold flex items-center gap-1"><Sparkles className="w-2.5 h-2.5" /> AI suggestions · tap to use</p>
                  {titleSuggestions.map((t, i) => (
                    <button key={i} type="button" onClick={() => { setForm(f => ({ ...f, title: t })); setTitleSuggestions([]); }}
                      data-testid={`title-suggestion-${i}`}
                      className="w-full text-left px-3 py-1.5 rounded-lg bg-white/[0.02] hover:bg-cyan-500/10 border border-white/[0.05] hover:border-cyan-400/30 text-white/85 hover:text-white text-[12.5px] transition-all">
                      {t}
                    </button>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <JewelField label="Category">
                  <JewelSelect value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    <option value="">— None —</option>
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </JewelSelect>
                </JewelField>
                <JewelField label="Slug (public URL)" hint={form.title && !form.slug ? `auto: /news/${(form.title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}` : ''}>
                  <JewelInput value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    placeholder="auto from title" />
                </JewelField>
              </div>
            </div>
          </section>

          {/* COVER IMAGE */}
          <section className="relative overflow-hidden rounded-2xl border p-4" style={section('#F59E0B')}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <ImageIcon className="w-3.5 h-3.5 text-amber-300" />
                <p className="text-[10px] tracking-[0.2em] uppercase text-amber-200/80 font-semibold">Cover image</p>
              </div>
              {imagePreview && (
                <button type="button" onClick={() => { setImagePreview(null); setImageFile(null); setForm(f => ({ ...f, image_url: '' })); }}
                  data-testid="clear-image-btn"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-rose-500/[0.10] border border-rose-500/30 text-rose-300 hover:bg-rose-500/[0.20] text-[10px] font-medium transition-all">
                  <Trash2 className="w-2.5 h-2.5" /> Remove
                </button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" onChange={(e) => handleImage(e.target.files?.[0])} className="hidden" data-testid="image-file-input" />
            {imagePreview ? (
              <div className="relative rounded-xl overflow-hidden group cursor-pointer" onClick={() => fileRef.current?.click()}>
                <img src={imagePreview} alt="cover" className="w-full h-48 object-cover" />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                  <span className="text-white text-sm inline-flex items-center gap-2"><Upload className="w-4 h-4" /> Replace</span>
                </div>
              </div>
            ) : (
              <div
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                data-testid="image-drop-zone"
                className={`rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-all ${dragOver ? 'border-amber-400/60 bg-amber-500/[0.08]' : 'border-white/[0.10] bg-white/[0.02] hover:border-amber-400/40'}`}>
                <Upload className="w-8 h-8 text-amber-300/60 mx-auto mb-2" />
                <p className="text-white/70 text-sm font-medium">Drop an image or click to upload</p>
                <p className="text-white/35 text-[11px] mt-1">PNG · JPG · WebP · up to 10MB</p>
              </div>
            )}
            <div className="mt-2">
              <input type="url" value={form.image_url} onChange={(e) => { setForm({ ...form, image_url: e.target.value }); setImagePreview(e.target.value || null); setImageFile(null); }}
                placeholder="…or paste an image URL"
                className="w-full px-3 py-2 bg-black/40 border border-white/[0.06] rounded-xl text-white text-xs placeholder-white/25 focus:outline-none focus:border-amber-400/40" />
            </div>
          </section>

          {/* CONTENT with AI */}
          <section className="relative overflow-hidden rounded-2xl border p-4" style={section('#8A2BE2')}>
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <div className="flex items-center gap-2">
                <FileText className="w-3.5 h-3.5 text-violet-300" />
                <p className="text-[10px] tracking-[0.2em] uppercase text-violet-200/80 font-semibold">Content</p>
                <span className="text-white/40 text-[10px]">· {words} words · {rt} min read</span>
              </div>
              <div className="flex items-center gap-1.5">
                <AIChip mode="content" busy={aiBusy} onClick={() => runAI('content')} icon={Wand2}
                  label={form.content ? 'Regenerate' : 'AI draft'} />
                {form.content && <AIChip mode="polish" busy={aiBusy} onClick={() => runAI('polish')} icon={Sparkles} label="Polish" />}
              </div>
            </div>
            <JewelTextarea rows={10} value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              placeholder="Write your article… or tap 'AI draft' to start from your title."
              data-testid="article-content-input" />
          </section>

          {/* EXCERPT */}
          <section className="relative overflow-hidden rounded-2xl border p-4" style={section('#10B981')}>
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <Info className="w-3.5 h-3.5 text-emerald-300" />
                <p className="text-[10px] tracking-[0.2em] uppercase text-emerald-200/80 font-semibold">Excerpt · summary card</p>
                <span className="text-white/40 text-[10px]">· {(form.excerpt || '').length}/200</span>
              </div>
              <AIChip mode="excerpt" busy={aiBusy} onClick={() => runAI('excerpt')} icon={Wand2} label="AI excerpt" />
            </div>
            <JewelTextarea rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value.slice(0, 220) })}
              placeholder="1-2 sentence summary shown in listings…" />
          </section>

          {/* TAGS */}
          <section className="relative overflow-hidden rounded-2xl border p-4" style={section('#EC4899')}>
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <Tag className="w-3.5 h-3.5 text-rose-300" />
                <p className="text-[10px] tracking-[0.2em] uppercase text-rose-200/80 font-semibold">Tags</p>
                {form.tags.length > 0 && <span className="text-white/40 text-[10px]">· {form.tags.length}</span>}
              </div>
              <AIChip mode="tags" busy={aiBusy} onClick={() => runAI('tags')} icon={Wand2} label="AI suggest" />
            </div>
            <div className="flex gap-2 mb-2">
              <input value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(tagInput); setTagInput(''); } }}
                placeholder="Type a tag and press Enter"
                data-testid="tag-input"
                className="flex-1 px-3 py-2 bg-black/40 border border-white/[0.06] rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-rose-400/40" />
              <button type="button" onClick={() => { addTag(tagInput); setTagInput(''); }}
                className="px-3 py-2 rounded-xl border border-rose-500/40 bg-rose-500/[0.15] text-rose-300 text-[11px] font-semibold">Add</button>
            </div>
            {form.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {form.tags.map(t => (
                  <span key={t} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full bg-rose-500/[0.10] border border-rose-400/25 text-rose-200 text-[10.5px] font-medium">
                    #{t}
                    <button type="button" onClick={() => removeTag(t)}
                      className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-rose-500/30">
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </section>

          {/* PUBLISH */}
          <section className="relative overflow-hidden rounded-2xl border p-4" style={section('#F43F5E')}>
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-3.5 h-3.5 text-rose-300" />
              <p className="text-[10px] tracking-[0.2em] uppercase text-rose-200/80 font-semibold">Publish</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <ToggleChip active={form.published} onClick={() => setForm(f => ({ ...f, published: !f.published }))}
                hex="#10B981" AI={Eye} II={EyeOff} activeLabel="Live on site" inactiveLabel="Save as draft"
                testid="publish-toggle" />
              <ToggleChip active={form.is_featured} onClick={() => setForm(f => ({ ...f, is_featured: !f.is_featured }))}
                hex="#F59E0B" AI={Star} II={Star} activeLabel="Featured" inactiveLabel="Mark featured"
                testid="featured-toggle" />
            </div>
          </section>
        </div>
      )}
    </JewelModal>
  );
};

const AIChip = ({ mode, busy, onClick, icon: Icon, label }) => {
  const b = busy === mode;
  return (
    <button type="button" onClick={onClick} disabled={!!busy}
      data-testid={`ai-news-${mode}`}
      className="group inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-violet-500/20 to-cyan-500/20 border border-violet-400/30 text-violet-100 hover:from-violet-500/35 hover:to-cyan-500/35 hover:border-violet-400/50 disabled:opacity-50 disabled:cursor-not-allowed text-[10.5px] font-semibold transition-all">
      {b ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
      <span>{label}</span>
      <Sparkles className="w-2.5 h-2.5 text-amber-300 opacity-70" />
    </button>
  );
};

const ToggleChip = ({ active, onClick, hex, AI, II, activeLabel, inactiveLabel, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border text-[11px] font-medium transition-all"
    style={{
      background: active ? `${hex}18` : 'rgba(255,255,255,0.03)',
      borderColor: active ? `${hex}55` : 'rgba(255,255,255,0.08)',
      color: active ? hex : 'rgba(255,255,255,0.55)',
      boxShadow: active ? `0 0 20px -8px ${hex}` : 'none',
    }}>
    {active ? <AI className="w-3.5 h-3.5" /> : <II className="w-3.5 h-3.5" />}
    {active ? activeLabel : inactiveLabel}
  </button>
);

const ArticlePreview = ({ form, imagePreview, rt }) => (
  <div className="rounded-2xl overflow-hidden border border-cyan-500/25 bg-[#0a0a14]" data-testid="article-preview">
    {imagePreview && (
      <div className="relative h-56 bg-slate-900">
        <img src={imagePreview} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <div className="absolute top-3 left-3 flex gap-2">
          {form.is_featured && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9.5px] font-bold uppercase tracking-wider bg-amber-500/25 border border-amber-400/50 text-amber-100 backdrop-blur-sm"><Star className="w-2.5 h-2.5" /> Featured</span>}
          {!form.published && <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[9.5px] font-bold uppercase tracking-wider bg-white/[0.10] border border-white/[0.15] text-white/80 backdrop-blur-sm">Draft</span>}
          {form.category && <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[9.5px] font-bold uppercase tracking-wider bg-black/50 border border-white/[0.15] text-white backdrop-blur-sm">{form.category}</span>}
        </div>
      </div>
    )}
    <div className="p-6">
      <h1 className="text-3xl font-semibold text-white tracking-tight leading-tight">{form.title || <span className="text-white/30 italic">Untitled</span>}</h1>
      <p className="text-white/40 text-[11px] mt-2">{rt} min read · {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
      {form.excerpt && (
        <p className="mt-4 text-white/70 text-base leading-relaxed border-l-2 border-cyan-400/50 pl-4 italic">{form.excerpt}</p>
      )}
      {form.content && (
        <div className="mt-5 prose prose-invert max-w-none">
          <p className="text-white/80 text-[14px] leading-[1.75] whitespace-pre-wrap">{form.content}</p>
        </div>
      )}
      {form.tags?.length > 0 && (
        <div className="mt-5 pt-4 border-t border-white/[0.06] flex flex-wrap gap-1.5">
          {form.tags.map(t => (
            <span key={t} className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/[0.08] text-white/60 text-[10.5px]">#{t}</span>
          ))}
        </div>
      )}
    </div>
  </div>
);

export default NewsComposer;
